import download from 'downloadjs';

type FileShareNavigator = Navigator & {
  canShare?: (data: { files?: File[] }) => boolean;
  share?: (data: { files?: File[]; title?: string }) => Promise<void>;
};

type DownloadResult = 'downloaded' | 'shared' | 'opened' | 'anchored' | 'cancelled' | 'skipped';

type DownloadStickerAssetOptions = {
  appWebView?: boolean;
  createObjectURL?: (blob: Blob) => string;
  documentRef?: Document;
  fetchFn?: typeof fetch;
  navigateTo?: (url: string) => void;
  navigatorRef?: FileShareNavigator;
  revokeObjectURL?: (url: string) => void;
  saveFile?: (data: Blob, fileName: string, mimeType?: string) => void;
  windowRef?: Window;
};

export const isStickerFairyAppWebView = () => {
  try {
    return localStorage.getItem('access') === 'app' || document.body.classList.contains('mw-in-app');
  } catch {
    return typeof document !== 'undefined' && document.body.classList.contains('mw-in-app');
  }
};

export const dataUrlToBlob = (fileUrl: string) => {
  const [metadata, data] = fileUrl.split(',');
  if (!metadata || !data || !metadata.startsWith('data:')) {
    return null;
  }

  const mimeMatch = metadata.match(/^data:([^;]+)/);
  const mimeType = mimeMatch?.[1] || 'application/octet-stream';
  const isBase64 = metadata.includes(';base64');
  const binary = isBase64 ? atob(data) : decodeURIComponent(data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }

  return new Blob([bytes], { type: mimeType });
};

const triggerAnchorDownload = (
  fileUrl: string,
  fileName: string,
  documentRef = document
) => {
  const anchor = documentRef.createElement('a');
  anchor.href = fileUrl;
  anchor.rel = 'noopener';
  anchor.setAttribute('download', fileName);
  documentRef.body.appendChild(anchor);
  anchor.click();
  requestAnimationFrame(() => {
    anchor.remove();
  });
};

const shareFileOnMobile = async (
  blob: Blob,
  fileName: string,
  navigatorRef: FileShareNavigator = navigator as FileShareNavigator
) => {
  if (!navigatorRef.share || !navigatorRef.canShare || typeof File === 'undefined') {
    return false;
  }

  const file = new File([blob], fileName, { type: blob.type || 'image/png' });
  if (!navigatorRef.canShare({ files: [file] })) {
    return false;
  }

  await navigatorRef.share({
    files: [file],
    title: fileName,
  });
  return true;
};

const openBlobInWebView = (
  blob: Blob,
  {
    createObjectURL = URL.createObjectURL,
    navigateTo = (url: string) => {
      window.location.href = url;
    },
    revokeObjectURL = URL.revokeObjectURL,
    windowRef = window,
  }: Pick<DownloadStickerAssetOptions, 'createObjectURL' | 'navigateTo' | 'revokeObjectURL' | 'windowRef'> = {}
) => {
  const objectUrl = createObjectURL(blob);
  navigateTo(objectUrl);
  windowRef.setTimeout(() => revokeObjectURL(objectUrl), 60000);
};

export const downloadStickerAsset = async (
  fileUrl: string,
  fileName: string,
  options: DownloadStickerAssetOptions = {}
): Promise<DownloadResult> => {
  if (!fileUrl) {
    return 'skipped';
  }

  const {
    appWebView = isStickerFairyAppWebView(),
    documentRef,
    fetchFn = fetch,
    navigatorRef,
    saveFile = download,
  } = options;

  let isSameOrigin = false;
  let isDataUrl = false;
  try {
    const url = new URL(fileUrl, window.location.href);
    isDataUrl = url.protocol === 'data:';
    isSameOrigin = url.origin === window.location.origin;
  } catch (error) {
    console.error('Invalid download URL:', error);
    triggerAnchorDownload(fileUrl, fileName, documentRef);
    return 'anchored';
  }

  const saveOrOpenBlob = async (blob: Blob) => {
    if (appWebView) {
      try {
        if (await shareFileOnMobile(blob, fileName, navigatorRef)) {
          return 'shared' as DownloadResult;
        }
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          return 'cancelled' as DownloadResult;
        }
        console.error('Error sharing sticker asset:', error);
      }

      openBlobInWebView(blob, options);
      return 'opened' as DownloadResult;
    }

    saveFile(blob, fileName, blob.type || 'image/png');
    return 'downloaded' as DownloadResult;
  };

  if (isDataUrl) {
    const blob = dataUrlToBlob(fileUrl);
    if (blob) {
      return saveOrOpenBlob(blob);
    }
  }

  if (!isSameOrigin) {
    triggerAnchorDownload(fileUrl, fileName, documentRef);
    return 'anchored';
  }

  try {
    const response = await fetchFn(fileUrl, {
      mode: 'cors',
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`Download failed with status ${response.status}`);
    }

    const blob = await response.blob();
    return saveOrOpenBlob(blob);
  } catch (error) {
    console.error('Error downloading sticker asset:', error);
    triggerAnchorDownload(fileUrl, fileName, documentRef);
    return 'anchored';
  }
};
